/**
 * Internal helper functions for functions.ts
 * These functions are only used internally within functions.ts and should not be exported from functions.ts.
 */

import { chunk } from 'lodash';
import {
  Listing,
  Listings,
  Coordinates,
  Bounds,
  ListingVal,
  ListingLegacy,
} from '../interfaces/idashboard';
import { FilterVal } from '../interfaces/iproptypes';
import {
  DATA_VERIFICATION_ATTEMPT_TRACKER_LS_KEY,
  amenitiesObject,
  AVAILABLE_COUNTRY,
  shortMonthNames,
  DEFAULT_FILTER_VALUES,
  sampleMarket,
} from '../utils/const';
import { callApi } from 'src/core/callApi';
const classifyPoint = require('robust-point-in-polygon');

// Helper functions (non-exported, used by other internal functions)
const areCoordinatesSame = (coord1, coord2) => {
  if (coord1.lat == coord2.lat && coord1.lon == coord2.lon) return true;
  return false;
};

const parseRing = (ringStr: string): Coordinates[] => {
  // Remove any leading/trailing parentheses
  const clean = ringStr
    .trim()
    .replace(/^\(/, '')
    .replace(/\)$/, '');
  const points = clean.split(',');

  const ring: Coordinates[] = [];

  points.forEach(p => {
    const [lngStr, latStr] = p.trim().split(/\s+/);
    const lng = parseFloat(lngStr);
    const lat = parseFloat(latStr);
    if (!isNaN(lng) && !isNaN(lat)) {
      ring.push({ lng, lat });
    }
  });

  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!areCoordinatesSame(first, last)) {
      ring.push({ ...first });
    }
  }

  return ring;
};

const toRad = (value: number): number => {
  return (value * Math.PI) / 180;
};

const toDegrees = (radians: number) => {
  return radians * (180 / Math.PI);
};

const getSuitableTextForMissingAndInactiveData = val => {
  if (val === 'missing') {
    return 'N/A';
  } else {
    return 'Inactive';
  }
};

const convertKeys = (obj: Record<string, any>): Record<string, any> => {
  const newObj: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const keyReversed = key.split('').reverse();
    const underscoreIndex = keyReversed.indexOf('_');
    underscoreIndex > -1 && keyReversed.splice(underscoreIndex, 1);
    let newKey = keyReversed.reverse().join('');
    if (newKey.startsWith('adr')) {
      newKey = 'daily_rate' + newKey.split('adr')[1];
    }
    if (newKey == 'supply') {
      newKey = 'active_listings_count';
    }
    newObj[newKey] = value;
  }
  return newObj;
};

const getCreditUsedThisMonth = (monthlyDict: Record<string, string[]>): number => {
  const now = new Date();
  const currentMonthKey = `${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`; // Format: 'MM-YYYY'
  const marketsThisMonth = monthlyDict[currentMonthKey] || [];
  return marketsThisMonth.length;
};

// Internal functions (exported for use in functions.ts)
const processMultiPolygon = (multiPolygonRaw: string): Coordinates[][] => {
  // Remove "MULTIPOLYGON" and outer parentheses
  multiPolygonRaw = multiPolygonRaw.replace('MULTIPOLYGON', '').trim(); // -> "(((...)),((...)))"
  multiPolygonRaw = multiPolygonRaw.replace(/^\(/, '').replace(/\)$/, ''); // strip first "(" and last ")"
  // Now content looks like "((...)),((...))"

  // Split into individual polygon blocks
  const polygonStrings = multiPolygonRaw.split(')),');

  const allRings: Coordinates[][] = [];
  polygonStrings.forEach(polyStr => {
    let poly = polyStr.trim();
    // Remove any leading/trailing parentheses that remain
    poly = poly.replace(/^\(/, '').replace(/\)$/, '');

    // Now poly looks like "(ring1),(ring2)" OR "ring1"
    const ringStrings = poly.split('),');

    ringStrings.forEach(r => {
      const ring = parseRing(r);
      if (ring.length > 0) {
        allRings.push(ring);
      }
    });
  });

  return allRings;
};

const getBoundedListings = (listings: Listings, bounds: Bounds): string[] => {
  if (!bounds || !Object.keys(listings).length) return [];

  const swBound: Coordinates = bounds.southWest;
  const neBound: Coordinates = bounds.northEast;

  const polygon = [
    [swBound.lat, swBound.lng],
    [swBound.lat, neBound.lng],
    [neBound.lat, neBound.lng],
    [neBound.lat, swBound.lng],
  ];

  let boundedListings: string[] = [];
  const listingEntries = Object.entries(listings);
  const batchSize = 50;

  const processBatch = (batch: [string, ListingVal][]) => {
    batch.forEach(([listingID, listing]) => {
      const point = [listing.latitude, listing.longitude];
      if (classifyPoint(polygon, point) === -1 || classifyPoint(polygon, point) === 0) {
        boundedListings.push(listingID);
      }
    });
  };

  const batches = chunk(listingEntries, batchSize);
  batches.forEach(processBatch);

  return boundedListings;
};

const checkHostType = (
  hostType: 'independent' | 'professional',
  hostListingCount: number
) => {
  if (hostType === 'independent') return hostListingCount < 5;
  else return hostListingCount >= 5;
};

const applyRevenueFilter = (
  revenueRange: { [key: number]: 'visible' | 'hidden' },
  filteredListings: string[],
  listings: Listings,
  revenueFilterValue: number[]
) => {
  const filteredRevListings: string[] = [];

  const revenueRangeVisibleCount = Object.keys(revenueRange).filter(
    key => revenueRange[key] === 'visible'
  ).length;

  if (revenueRangeVisibleCount === 0) {
    return [];
  }

  if (revenueRangeVisibleCount === 4) {
    return filteredListings;
  }

  const revPotentialRangeArray: number[] = Object.keys(revenueRange)
    .map(Number)
    .sort((a, b) => a - b);

  let cntObj = {};
  const listingIDs = filteredListings;
  for (let listingID of listingIDs) {
    const listing = listings[listingID];
    const len = 4;

    for (let i = len; i >= 0; i--) {
      if (
        i !== 0 &&
        listing.revenuePotential <= revPotentialRangeArray[i] &&
        listing.revenuePotential > revPotentialRangeArray[i - 1] &&
        revenueRange[revPotentialRangeArray[i]] === 'visible'
      ) {
        filteredRevListings.push(listingID);
        break;
      }

      if (
        i === 0 &&
        listing.revenuePotential <= revPotentialRangeArray[i] &&
        revenueRange[revPotentialRangeArray[i]] === 'visible'
      ) {
        filteredRevListings.push(listingID);
        break;
      }
    }
  }
  return filteredRevListings;
};

const applyPriceTierFilter = (
  filteredListings: string[],
  listings: Listings,
  priceTierFilterValue: number[][]
) => {
  const filteredPriceTierListings: string[] = [];

  if (priceTierFilterValue.length == 0) return filteredPriceTierListings;

  const highLowValue = arrayOfArrays => {
    const minValue = arrayOfArrays.reduce((min, arr) => Math.min(min, arr[0]), arrayOfArrays[0][0]);
    const maxValue = arrayOfArrays.reduce((max, arr) => Math.max(max, arr[1]), arrayOfArrays[0][1]);
    return [minValue, maxValue];
  };

  const [lowPrice, highPrice] = highLowValue(priceTierFilterValue);

  for (let listingID of filteredListings) {
    const listing = listings[listingID];
    if (listing.adrLTM && listing.adrLTM < highPrice && listing.adrLTM >= lowPrice) {
      filteredPriceTierListings.push(listingID);
    }
  }

  return filteredPriceTierListings;
};

const jsonArrayToCSV = (
  data: {
    [key: string]: number | string;
  }[],
  addHeader = true
) => {
  let csv = data.map(row => Object.values(row));
  addHeader === true && csv.unshift(Object.keys(data[0]));
  return `"${csv.join('"\n"').replace(/,/g, '","')}"`;
};

const getPropertyTypeFreq = (listings: Listing[], bookingValsFlag = false) => {
  let propertyTypeFreq = {
    entire_home: 0,
    condo: 0,
    private_room: 0,
    hotel_room: 0,
    shared_room: 0,
  };
  let propertyTypeBookingVals = {
    entire_home: 0,
    condo: 0,
    private_room: 0,
    hotel_room: 0,
    shared_room: 0,
  };

  listings.forEach((listing: Listing) => {
    if (listing.room_type === 'entire_home' && listing.property_type === 'condo') {
      propertyTypeFreq.condo = propertyTypeFreq.condo + 1;
      if (bookingValsFlag) {
        listing.bookings.forEach(booking => {
          propertyTypeBookingVals.condo = propertyTypeBookingVals.condo + booking.totalValue;
        });
      }
    } else {
      propertyTypeFreq[listing.room_type] = propertyTypeFreq[listing.room_type] + 1;
      if (bookingValsFlag) {
        listing.bookings.forEach(booking => {
          propertyTypeBookingVals[listing.room_type] =
            propertyTypeBookingVals[listing.room_type] + booking.totalValue;
        });
      }
    }
  });

  if (bookingValsFlag) {
    Object.keys(propertyTypeBookingVals).forEach((propertyType: string) => {
      propertyTypeBookingVals[propertyType] = Math.round(
        propertyTypeBookingVals[propertyType] /
          (propertyTypeFreq[propertyType] > 0 ? propertyTypeFreq[propertyType] : 1)
      );
    });

    return propertyTypeBookingVals;
  }

  return propertyTypeFreq;
};

const getPropertySizeFreq = (listings: Listing[], bookingValsFlag = false) => {
  let propertySizeFreq = {
    '0': 0,
    '1': 0,
    '2': 0,
    '3': 0,
    '4': 0,
    '5': 0,
    '6': 0,
    rooms: 0,
  };
  let propertySizeBookingVals = {
    '0': 0,
    '1': 0,
    '2': 0,
    '3': 0,
    '4': 0,
    '5': 0,
    '6': 0,
    rooms: 0,
  };

  listings.forEach((listing: Listing) => {
    // console.log('listing: ', listing);
    if (
      ((listing.room_type && listing.room_type === 'entire_home') ||
        (listing.property_type && listing.property_type === 'condo')) &&
      (listing.bedrooms == 'Studio' || !isNaN(Number(listing.bedrooms)))
    ) {
      if (listing.bedrooms == 'Studio') {
        propertySizeFreq['0'] = propertySizeFreq['0'] + 1;
        if (bookingValsFlag) {
          listing.bookings.forEach(bookingData => {
            propertySizeBookingVals['0'] = propertySizeBookingVals['0'] + bookingData.totalValue;
          });
        }
      } else if (Number(listing.bedrooms) >= 6) {
        propertySizeFreq['6'] = propertySizeFreq['6'] + 1;
        if (bookingValsFlag) {
          listing.bookings.forEach(bookingData => {
            propertySizeBookingVals['6'] = propertySizeBookingVals['6'] + bookingData.totalValue;
          });
        }
      } else {
        propertySizeFreq[listing.bedrooms] = propertySizeFreq[listing.bedrooms] + 1;
        if (bookingValsFlag) {
          listing.bookings.forEach(bookingData => {
            propertySizeBookingVals[listing.bedrooms] =
              propertySizeBookingVals[listing.bedrooms] + bookingData.totalValue;
          });
        }
      }
    } else if (
      listing.room_type &&
      (listing.room_type === 'private_room' ||
        listing.room_type === 'hotel_room' ||
        listing.room_type === 'shared_room')
    ) {
      propertySizeFreq['rooms'] = propertySizeFreq['rooms'] + 1;
      if (bookingValsFlag) {
        listing.bookings.forEach(bookingData => {
          propertySizeBookingVals['rooms'] =
            propertySizeBookingVals['rooms'] + bookingData.totalValue;
        });
      }
    }
  });

  if (bookingValsFlag) {
    // Taking avg of booking values for each property size
    Object.keys(propertySizeBookingVals).forEach(bed => {
      propertySizeBookingVals[bed] =
        propertySizeFreq[bed] > 0
          ? Math.round(propertySizeBookingVals[bed] / propertySizeFreq[bed])
          : 0;
    });
  }

  return bookingValsFlag ? propertySizeBookingVals : propertySizeFreq;
};

const getBookingTypeAndLeadFreq = (listings: Listing[] | ListingLegacy[]) => {
  let bookingTypesFreq = {
    'Single Night': 0, //1 night
    'Multiple Nights': 0, //2 - 6 nights
    Weekly: 0, // 7 - 15 nights
    'Mid-term': 0, // 15+ nights
  };

  let bookingLeadsFreq = {
    'Last-minute': 0, // <= 7
    Normal: 0, // 8 - 30
    Advanced: 0, // 30+
  };

  listings.forEach(listing => {
    listing.bookings.forEach(booking => {
      const bookingType = booking.numberNights;
      if (bookingType > 0) {
        if (bookingType === 1) {
          bookingTypesFreq['Single Night'] = bookingTypesFreq['Single Night'] + 1;
        } else if (bookingType > 1 && bookingType <= 6) {
          bookingTypesFreq['Multiple Nights'] = bookingTypesFreq['Multiple Nights'] + 1;
        } else if (bookingType > 6 && bookingType <= 15) {
          bookingTypesFreq['Weekly'] = bookingTypesFreq['Weekly'] + 1;
        } else {
          bookingTypesFreq['Mid-term'] = bookingTypesFreq['Mid-term'] + 1;
        }
      }

      const bookingLead = booking.bookingLead;
      if (bookingLead > 0) {
        if (bookingLead <= 7) {
          bookingLeadsFreq['Last-minute'] = bookingLeadsFreq['Last-minute'] + 1;
        } else if (bookingLead > 7 && bookingLead <= 30) {
          bookingLeadsFreq['Normal'] = bookingLeadsFreq['Normal'] + 1;
        } else {
          bookingLeadsFreq['Advanced'] = bookingLeadsFreq['Advanced'] + 1;
        }
      }
    });
  });

  return [bookingTypesFreq, bookingLeadsFreq];
};

const getUserFriendlyPropertyTypeName = (
  propertyType: string,
  listingObject: Listing | ListingLegacy = {} as Listing | ListingLegacy
) => {
  // Check if listingObject is not empty (has properties)
  const hasProperties = listingObject && Object.keys(listingObject).length > 0;
  if (
    hasProperties &&
    listingObject.room_type === 'entire_home' &&
    listingObject.property_type === 'condo'
  ) {
    return 'Condo';
  } else if (propertyType === 'entire_home') {
    return 'Entire Home';
  } else if (propertyType === 'condo') {
    return 'Condo';
  } else if (propertyType === 'private_room') {
    return 'Private Room';
  } else if (propertyType === 'shared_room') {
    return 'Shared Room';
  } else if (propertyType === 'hotel_room') {
    return 'Hotel Room';
  } else return propertyType;
};

const normalizeLongitude = (longitude: number) => {
  longitude = longitude % 360; // Wrap around the world
  if (longitude <= -180) {
    return longitude + 360;
  } else if (longitude > 180) {
    return longitude - 360;
  } else {
    return longitude;
  }
};

const trackUserDataVerificationAttempt = (market: string): Boolean => {
  const key = DATA_VERIFICATION_ATTEMPT_TRACKER_LS_KEY;
  let trackerArray: string[] = [];
  let tracked = true;

  if (market.toLowerCase() === sampleMarket) return false;

  if (localStorage.getItem(key) == null) {
    tracked = false;
  } else {
    const existingTracker = localStorage.getItem(key) as string;
    trackerArray = JSON.parse(existingTracker);
    tracked = trackerArray.includes(market) ? true : false;
  }

  if (!tracked) {
    trackerArray.push(market);
    localStorage.setItem(key, JSON.stringify(trackerArray));
    const api =
      'https://qdo7trqmn7.execute-api.us-east-2.amazonaws.com/default/trackProductAnalytics';
    const config = { dataVerificationAttemptedMarket: market };
    callApi(api, { queryParams: config }, true);
    return true;
  }

  return false;
};

const initializeAmenityDistributionByPropertySize = () => {
  return {
    studio: {
      present: [],
      absent: [],
      total: 0,
    },
    1: {
      present: [],
      absent: [],
      total: 0,
    },
    2: {
      present: [],
      absent: [],
      total: 0,
    },
    3: {
      present: [],
      absent: [],
      total: 0,
    },
    4: {
      present: [],
      absent: [],
      total: 0,
    },
    5: {
      present: [],
      absent: [],
      total: 0,
    },
    '6+': {
      present: [],
      absent: [],
      total: 0,
    },
  };
};

const dateDeforeNDays = noOfDaysBefore => {
  const currentDate = new Date();
  const dateBeforeNDays = new Date();
  dateBeforeNDays.setDate(currentDate.getDate() - noOfDaysBefore);
  return dateBeforeNDays;
};

const convertDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date
    .getDate()
    .toString()
    .padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const processListingData = listings => {
  const processedListings: any[] = [];
  for (const listing of listings) {
    try {
      const revPAN = listing['active_days_count_ltm']
        ? Math.round(listing['annual_revenue_ltm'] / listing['active_days_count_ltm'])
        : 0;
      const revenueFromCleaningFeeLTM = listing['cleaning_fee']
        ? Math.round(listing['cleaning_fee'] * listing['no_of_bookings_ltm'])
        : 0;

      const retListing = {
        accommodates: listing['accommodates'],
        activeDaysCount: listing['active_days_count_ltm'],
        adrLTM: listing['avg_booked_daily_rate_ltm'],
        amenities: listing['amenities'],
        annualRevenueLTM: listing['annual_revenue_ltm'] ? listing['annual_revenue_ltm'] : 'N/A',
        bathrooms: listing['bathrooms'],
        bedrooms: listing['bedrooms'],
        cancellation_policy: listing['cancellation_policy'],
        cleaningFee: listing['cleaning_fee'],
        extraGuestFee: listing['extra_fee_person_estimate'],
        hasLicense: listing['license'] === 't' ? true : false,
        hostId: listing['host_id'],
        hostListingCount: listing['hostListingCount'],
        host_name: listing['host_name'],
        last_seen: listing['last_seen'],
        latitude: listing['latitude'],
        listingID: listing['listingID'],
        longitude: listing['longitude'],
        minimum_nights: listing['minimum_nights'],
        name: listing['name'],
        numberOfBookingsLTM: listing['no_of_bookings_ltm'],
        occupancyRateLTM: Math.min(listing['avg_occupancy_rate_ltm'], 95),
        property_type: listing['room_and_property_type'],
        revPAN: revPAN,
        revenueFromCleaningFeeLTM: revenueFromCleaningFeeLTM,
        revenuePotential: listing['revenue_potential'],
        review_scores_rating: Math.round(listing['reveiw_scores_rating'] / 20),
        room_type: listing['room_and_property_type'],
        thumbnail_url: listing['thumbnail_url'],
        visible_review_count: listing['visible_review_count'],
        rest_thumbnails: listing['thumbnail_url_extended'],
        added_on: listing['added_on'],
        bookingComURL: listing['booking_com_url'],
        vrboURL: listing['vrbo_url'],
      };

      processedListings.push(retListing);
    } catch (error) {
      console.log('Error while loading marker:', error);
    }
  }
  return processedListings;
};

const processBookingListingData = listings => {
  const processedListings: any[] = [];
  for (const listing of listings) {
    try {
      const listingName = listing['listing_name'];
      const countryCode = listing['country_code'];
      const listingURL = `https://www.booking.com/hotel/${countryCode}/${listingName}.en-gb.html`;

      const retListing = {
        activeDaysCount: listing['active_days_count_ltm'],
        adrLTM: listing['avg_booked_daily_rate_ltm'],
        annualRevenueLTM: listing['annual_revenue_ltm'] ? listing['annual_revenue_ltm'] : 'N/A',
        bedrooms: listing['bedrooms'],
        latitude: listing['latitude'],
        listingID: listing['listingID'],
        longitude: listing['longitude'],
        name: listing['name'],
        listingURL: listingURL,
        occupancyRateLTM: Math.min(listing['avg_occupancy_rate_ltm'], 95),
        property_type: listing['room_and_property_type'],
        revenuePotential: listing['revenue_potential'],
        review_scores_rating: listing['star_rating'],
        room_type: listing['room_and_property_type'],
        thumbnail_url: listing['thumbnail_url'],
        visible_review_count: listing['visible_review_count'],
      };

      processedListings.push(retListing);
    } catch (error) {
      console.log('Error while loading marker:', error);
    }
  }
  return processedListings;
};

const isMiddleEastTimezone = timezone => {
  const middleEastTimezones = [
    'Asia/Dubai',
    'Asia/Beirut',
    'Asia/Amman',
    'Asia/Jerusalem',
    'Asia/Kuwait',
    'Asia/Riyadh',
    'Asia/Baghdad',
    'Asia/Bahrain',
    'Asia/Qatar',
    'Asia/Damascus',
    'Asia/Muscat',
    'Asia/Aden',
    'Asia/Tehran',
  ];

  return middleEastTimezones.includes(timezone);
};

const normalizeCountryKey = (value: string) => value.toLowerCase().replace(/[\s-]/g, '');

const buildCountryLookupMaps = (countryMap: Record<string, string>) => {
  const lookupByName: Record<string, { name: string; code: string }> = {};
  const lookupByCode: Record<string, { name: string; code: string }> = {};

  for (const code in countryMap) {
    const name = countryMap[code];
    const normalizedName = normalizeCountryKey(name);
    const normalizedCode = normalizeCountryKey(code);
    lookupByName[normalizedName] = { code, name };
    lookupByCode[normalizedCode] = { code, name };
  }

  return { lookupByName, lookupByCode };
};

const capitalizeText = (name: string): string => {
  let nameArray: string[] = name.split(' ');
  let capitalizedName: string = '';
  nameArray.forEach(item => {
    capitalizedName += item.charAt(0).toUpperCase() + item.slice(1) + ' ';
  });
  return capitalizedName.trim();
};

// Export all functions for use in functions.ts
export {
  // Helper functions
  areCoordinatesSame,
  parseRing,
  toRad,
  toDegrees,
  getSuitableTextForMissingAndInactiveData,
  convertKeys,
  getCreditUsedThisMonth,
  // Internal functions
  processMultiPolygon,
  getBoundedListings,
  checkHostType,
  applyRevenueFilter,
  applyPriceTierFilter,
  jsonArrayToCSV,
  getPropertyTypeFreq,
  getPropertySizeFreq,
  getBookingTypeAndLeadFreq,
  getUserFriendlyPropertyTypeName,
  normalizeLongitude,
  trackUserDataVerificationAttempt,
  initializeAmenityDistributionByPropertySize,
  dateDeforeNDays,
  convertDateToString,
  processListingData,
  processBookingListingData,
  isMiddleEastTimezone,
  normalizeCountryKey,
  buildCountryLookupMaps,
  capitalizeText,
};

